/**
 * What the YClients banner says (boona docs/codebase/imports/yclients/full/README.md §8,
 * onboarding.md D1). Pure, so it is tested without rendering.
 *
 * `mode`: sync (linked, synced) · importing (the full import was asked for — we run it by
 * hand) · finish_full (the administrator step is open) · partial (a simple import finished).
 * Older shards send no mode: that is `sync`. A paused sync = the import stopped (`stop_reason`
 * license: the salon's YClients licence ended · access: YClients no longer lets us read it):
 * the owner switches Boona to full mode themselves, in the dashboard's Settings → YClients.
 */

export type YSyncMode = "sync" | "importing" | "finish_full" | "partial";

export type YSyncState = {
  active: boolean;
  mode?: YSyncMode;
  interval_hours?: number;
  last_synced_at?: string | null;   // UTC ISO
  paused?: boolean;
  stop_reason?: "license" | "access" | null;
  settings_path?: string;
  import_url?: string;              // id.boona.app — the full import page
  company_id?: number;              // the salon — what the ✕ on "partial" remembers
};

export type YSyncBannerView = {
  message: string;
  tone: "info" | "warn" | "ok";
  /** the button: a dashboard page (sync) or the full import on id.boona.app; none while importing */
  cta: { label: string; dashboardPath?: string; href?: string } | null;
};

type T = (key: string, fallback: string, opts?: Record<string, unknown>) => string;

/** A UTC timestamp as the viewer's wall clock, e.g. "29.09.2026, 14:05". */
export function syncedAtLabel(iso: string | null | undefined, locale?: string): string | null {
  if (!iso) return null;
  const moment = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);   // naive = UTC
  if (Number.isNaN(moment.getTime())) return null;
  return moment.toLocaleString(locale, { day: "2-digit", month: "2-digit", year: "numeric",
                                         hour: "2-digit", minute: "2-digit" });
}

/**
 * The localStorage key that hides this banner, or null when it can't be hidden. Only
 * "partial" can: it asks for nothing urgent, and its "Import everything" got clicked just
 * to make it go away (2026-10-29). Per salon, per browser — the dashboard's ✕ uses the
 * same key on its own origin.
 */
export function hideKey(data: YSyncState | null | undefined): string | null {
  if (!data?.active || data.mode !== "partial" || !data.company_id) return null;
  return `boona.ysync_partial.hidden.${data.company_id}`;
}

export function ysyncBannerView(data: YSyncState, t: T, locale?: string): YSyncBannerView | null {
  if (!data.active) return null;
  const mode = data.mode ?? "sync";
  if (mode === "importing") {
    return {
      message: t("ysync.banner_importing",
        "Your data is moving over from YClients. Every part of Boona updates once it finishes — usually within 24 hours. We'll contact you."),
      tone: "ok",
      cta: null,
    };
  }
  if (mode === "finish_full" || mode === "partial") {
    return {
      message: mode === "finish_full"
        ? t("ysync.banner_finish_full",
            "One step left for the full import from YClients: add our administrator — it takes a couple of minutes.")
        : t("ysync.banner_partial",
            "You brought part of your data from YClients: services, masters and schedule. Bring the rest — clients, visit history, payments — for free."),
      tone: "warn",
      cta: data.import_url
        ? { label: mode === "finish_full" ? t("ysync.banner_finish_cta", "Finish") : t("ysync.banner_partial_cta", "Import everything"),
            href: data.import_url }
        : null,
    };
  }
  const message = ysyncBannerMessage(data, t, locale);
  return message
    ? { message, tone: data.paused ? "warn" : "info",
        cta: { label: data.paused ? t("ysync.banner_full_mode_cta", "Switch to full mode") : t("ysync.banner_cta", "Settings"),
               dashboardPath: data.settings_path || "/dashboard/settings/yclients" } }
    : null;
}

export function ysyncBannerMessage(data: YSyncState, t: T, locale?: string): string | null {
  if (!data.active) return null;
  if (data.paused) {
    return data.stop_reason === "license"
      ? t("ysync.banner_stopped_license",
          "The import from YClients has stopped: your salon's YClients licence has ended. Everything imported stays here. Switch Boona to full mode — your notifications to clients come back on.")
      : t("ysync.banner_stopped_access",
          "The import from YClients has stopped: YClients no longer lets us read your salon. Everything imported stays here. Switch Boona to full mode — your notifications to clients come back on.");
  }
  const main = t("ysync.banner_on",
        "The YClients sync is on: every {{hours}} h your YClients data is copied here and overwrites the same records changed in Boona. Boona sends your clients no messages meanwhile.",
        { hours: data.interval_hours ?? 3 });
  const at = syncedAtLabel(data.last_synced_at, locale);
  return at ? `${main} ${t("ysync.banner_last", "Last update: {{at}}.", { at })}` : main;
}
